/**
 * Клиент серверной части.
 *
 * Всё, кроме профиля дела, живёт на сервере: генерация, шаблоны, справочники,
 * подсказки DaData, резервные копии и журнал. Профиль читается и пишется
 * напрямую в поле сделки (`storage/cases.ts`) — так его сохранность
 * не зависит от доступности сервера (ТЗ п. 8.2).
 *
 * Подтверждение подлинности запроса (ТЗ п. 8.3.5): в заголовках уходят домен
 * портала и ключ сеанса, выданный BX24.js. Сервер проверяет их обращением
 * к порталу — подставить чужой идентификатор пользователя нельзя.
 */

import type { CaseProfile, Declension } from '@bd/domain';
import type { DocumentKind } from '@bd/doc-engine';
import { bx24 } from './bx24.js';

/** Организация из DaData, разложенная сервером по полям справочника (FR-13). */
export interface PartySuggestion {
  label: string;
  entry: { name: string; inn: string; ogrn?: string; address: string; source: string };
  warning: string | null;
}

/** Ошибка с перечнем причин — интерфейс показывает их списком (ТЗ п. 8.2). */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly details: string[] = [],
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface ErrorBody {
  error?: { code?: string; message?: string; details?: string[]; requestId?: string };
}

/** Заголовки подтверждения: домен портала и ключ текущего сеанса. */
function authHeaders(): Record<string, string> {
  const api = bx24();
  const auth = api?.getAuth();
  const headers: Record<string, string> = {};
  if (auth) {
    headers['X-B24-Domain'] = auth.domain;
    headers['X-B24-Auth'] = auth.access_token;
    headers['X-B24-Member'] = auth.member_id;
  }
  return headers;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      ...init,
      headers: { ...authHeaders(), ...(init.headers as Record<string, string> | undefined) },
    });
  } catch {
    // Недоступность сервера не должна выглядеть технической аварией:
    // введённые данные при этом целы, они в карточке дела
    throw new ApiError(
      'Серверная часть недоступна. Введённые данные сохранены в карточке дела, повторите позже',
      'SERVER_UNAVAILABLE',
    );
  }

  if (response.status === 204) return undefined as T;

  const body = (await response.json().catch(() => ({}))) as T & ErrorBody;

  if (!response.ok) {
    const error = body.error ?? {};
    throw new ApiError(
      error.message ?? 'Не удалось выполнить запрос',
      error.code ?? `HTTP_${response.status}`,
      error.details ?? [],
      error.requestId,
    );
  }

  return body as T;
}

/**
 * Чтение с сервера — с повтором при обрыве связи.
 *
 * Связь с сервером у заказчика рвётся коротко и часто: пять обрывов
 * за рабочий день, каждый по несколько секунд. Одна неудачная попытка
 * означала пустой справочник судов и тревожную полосу через всю карточку —
 * при том что через две секунды всё работает. Юрист в этот момент видит
 * «собрать пакет не удастся» и идёт звонить администратору по поводу,
 * которого уже нет.
 *
 * Повторяется ТОЛЬКО чтение. Записи (сохранение, генерация, утверждение)
 * повторять вслепую нельзя: запрос мог дойти до сервера и выполниться,
 * а оборваться уже на ответе — и повтор создал бы вторую папку с пакетом
 * либо вторую запись в журнале.
 *
 * Повтор только при недоступности сервера. Отказ по существу — нет прав,
 * не найдено, неверные данные — повторять бессмысленно: ответ будет тот же.
 */
const RETRY_DELAYS_MS = [400, 1200];

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function get<T>(path: string): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await request<T>(path);
    } catch (error) {
      const unavailable = error instanceof ApiError && error.code === 'SERVER_UNAVAILABLE';
      if (!unavailable || attempt >= RETRY_DELAYS_MS.length) throw error;
      await wait(RETRY_DELAYS_MS[attempt] as number);
    }
  }
}

const send = <T>(path: string, body: unknown, method = 'POST') =>
  request<T>(path, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });

/** Загрузка файла: заголовок Content-Type проставляет браузер сам. */
const upload = <T>(path: string, form: FormData) => request<T>(path, { method: 'POST', body: form });

export const api = {
  blankProfile: (caseId: string) => get<{ profile: CaseProfile }>(`/cases/${caseId}/blank`),

  validate: (profile: CaseProfile) =>
    send<{ ok: boolean; errors: string[]; warnings: string[] }>('/cases/validate', { profile }),

  // ---------------------------------------------------------- блокировка

  lock: (caseId: string) =>
    send<{ acquired: boolean; heldBy: string | null; readOnly: boolean }>(`/cases/${caseId}/lock`, {}),

  unlock: (caseId: string) => request<{ released: boolean }>(`/cases/${caseId}/lock`, { method: 'DELETE' }),

  // ------------------------------------------------ резервная копия профиля

  /**
   * Копия профиля на сервере (ТЗ п. 5.1). Хранится зашифрованной, срок
   * хранения задаётся настройкой сервера. Отсюда же пишется журнал.
   */
  backup: (caseId: string, profile: CaseProfile) =>
    send<{ saved: boolean; revision: number }>(`/cases/${caseId}/backup`, { profile }),

  backups: (caseId: string) =>
    get<{ backups: Array<{ revision: number; createdAt: string; expiresAt: string }> }>(`/cases/${caseId}/backups`),

  restoreBackup: (caseId: string, revision: number) =>
    send<{ profile: CaseProfile }>(`/cases/${caseId}/backups/${revision}/restore`, {}),

  journal: (caseId: string) =>
    get<{ entries: Array<{ createdAt: string; userId: string | null; action: string; comment: string | null }> }>(
      `/cases/${caseId}/journal`,
    ),

  // ---------------------------------------------------------- генерация

  generate: (body: {
    caseId: string;
    profile: CaseProfile;
    documents?: DocumentKind[];
    formats?: Array<'docx' | 'pdf'>;
    folderId?: string;
    templateVersions?: Partial<Record<DocumentKind, number>>;
  }) =>
    send<{
      packageVersion: number;
      folderId: string;
      files: Array<{ kind: DocumentKind; fileName: string; format: 'docx' | 'pdf'; downloadUrl?: string }>;
      warnings: string[];
      durationMs: number;
    }>('/generate', body),

  versions: (caseId: string, folderId?: string) =>
    get<{ folderId: string; versions: Array<{ version: number; files: Array<{ id: string; name: string; downloadUrl?: string }> }> }>(
      `/cases/${caseId}/versions${folderId ? `?folderId=${encodeURIComponent(folderId)}` : ''}`,
    ),

  preview: (profile: CaseProfile, documentKind: DocumentKind = 'zayavlenie', previous?: string[]) =>
    send<{ paragraphs: string[]; diff: Array<{ status: string; text: string }> | null; templateVersion: number }>(
      '/preview',
      { profile, documentKind, previous },
    ),

  // ---------------------------------------------------------- склонения

  decline: (
    name: { last: string; first: string; middle?: string; gender: 'male' | 'female' | null },
    current?: Declension,
  ) => send<{ declension: Declension }>('/linguistics/decline', { name, current }),

  // -------------------------------------------------------- справочники

  dictionary: (name: string, params: { search?: string; limit?: number } = {}) => {
    const query = new URLSearchParams();
    if (params.search) query.set('search', params.search);
    if (params.limit) query.set('limit', String(params.limit));
    const suffix = query.toString();
    return get<{ entries: Array<Record<string, unknown> & { id: string; name: string }> }>(
      `/dictionaries/${name}${suffix ? `?${suffix}` : ''}`,
    );
  },

  saveDictionaryEntry: (name: string, entry: Record<string, unknown>) =>
    send<{ entry: Record<string, unknown> }>(`/dictionaries/${name}`, entry),

  archiveDictionaryEntry: (name: string, id: string, archived: boolean) =>
    send<{ entry: Record<string, unknown> }>(`/dictionaries/${name}/${id}/archive`, { archived }),

  /** Предзаполнение суда по субъекту РФ (правило R-01). */
  courtByRegion: (regionCode: string) =>
    get<{ court: (Record<string, unknown> & { name: string; address: string }) | null }>(
      `/dictionaries/courts/by-region/${regionCode}`,
    ),

  // ------------------------------------------------------------ шаблоны

  templateVersions: (kind: DocumentKind) =>
    get<{
      versions: Array<{
        id: string;
        version: number;
        uploadedAt: string;
        uploadedBy: string | null;
        isCurrent: boolean;
        comment: string | null;
      }>;
    }>(`/templates/${kind}/versions`),

  uploadTemplate: (kind: DocumentKind, file: File, comment: string) => {
    const form = new FormData();
    form.append('comment', comment);
    form.append('file', file, file.name);
    return upload<{ version: number; warnings: string[] }>(`/templates/${kind}`, form);
  },

  /** Проверка разметки без загрузки (FR-42). */
  checkTemplateFile: (file: File) => {
    const form = new FormData();
    form.append('file', file, file.name);
    return upload<{
      ok: boolean;
      issues: Array<{ severity: 'error' | 'warning'; message: string }>;
      variables: string[];
      sections: string[];
    }>('/templates/check', form);
  },

  setCurrentTemplate: (kind: DocumentKind, version: number) =>
    send<{ version: number }>(`/templates/${kind}/${version}/current`, {}),

  // ------------------------------------------------------------- DaData

  dadata: {
    status: () =>
      get<{ enabled: boolean; addressSuggestions: boolean; cleanerAvailable: boolean }>('/dadata/status'),

    party: (query: string) =>
      send<{ enabled: boolean; suggestions: PartySuggestion[] }>('/dadata/party/suggest', { query }),

    findByInn: (inn: string) => send<{ suggestions: PartySuggestion[] }>('/dadata/party/find', { inn }),

    /**
     * Ввоз организации в справочник кредиторов (FR-13). Источником данных для
     * документов остаётся справочник, а не DaData: он переживёт отключение
     * внешнего сервиса.
     */
    importParty: (inn: string) =>
      send<{ entry: Record<string, unknown> & { id: string; name: string }; created: boolean; warning: string | null }>(
        '/dadata/party/import',
        { inn },
      ),

    bank: (query: string) =>
      send<{ enabled: boolean; suggestions: Array<{ label: string; bankName: string; bic: string }> }>(
        '/dadata/bank/suggest',
        { query },
      ),
  },
};
